'use client';

import { DownIcon } from '@wolfejs/icons/IconMap';
import { Button } from '@wolfejs/ui/Button';
import { CSVDownloadLink } from '@wolfejs/ui/CSVDocument';
import { Divider } from '@wolfejs/ui/Divider';
import type { MenuItemType } from '@wolfejs/ui/Menu';
import { MenuItem, type MainMenuItemRecursive } from '@wolfejs/ui/Menu';
import { PopoverMenu } from '@wolfejs/ui/PopoverMenu';
import type { RadioItem } from '@wolfejs/ui/RadioGroupList/RadioGroupList';
import { RadioGroupList } from '@wolfejs/ui/RadioGroupList/RadioGroupList';
import type { SelectOption, SingleValue } from '@wolfejs/ui/Select';
import { Select } from '@wolfejs/ui/Select';
import { UISizes } from '@wolfejs/ui/types/UI';
import isEmpty from 'lodash/isEmpty';
import { GlobalSearchBox } from '../GlobalSearchBox/GlobalSearchBox';

export type TableFiltersProps = {
  statusFilters?: RadioItem[];
  dateFilterOptions?: SelectOption<string>[];
  filterOptions?: SelectOption<string>[];
  defaultDateFilterOption?: SelectOption<string>;
  defaultFilterOption?: SelectOption<string>;
  popoverActionMenuItems?: MainMenuItemRecursive[];
  onPopoverActionMenuSelect?: (item: MenuItemType) => void;
  exportData?: object[];
  globalSearchValue?: string;
  onGlobalSearchChange?: (value: string) => void;
  onDateFilterChange?: (item: SingleValue<{ label: React.ReactNode; value?: string }>) => void;
  onFilterChange?: (item: SingleValue<{ label: React.ReactNode; value?: string }>) => void;
  onStatusFilterChange?: (value: string) => void;
  handleGenerateReport?: () => void;
  selectedStatusValue?: string;
};

export const TableFilters = ({
  statusFilters,
  dateFilterOptions,
  defaultDateFilterOption,
  globalSearchValue,
  onGlobalSearchChange,
  onDateFilterChange,
  popoverActionMenuItems,
  onPopoverActionMenuSelect,
  exportData = [],
  onStatusFilterChange,
  selectedStatusValue,
}: TableFiltersProps) => {
  let _popoverActionMenuItems: MainMenuItemRecursive[] = [];

  if (!isEmpty(exportData)) {
    _popoverActionMenuItems = [
      ...(popoverActionMenuItems && popoverActionMenuItems.length > 0 ? popoverActionMenuItems : []),
      {
        text: 'Export to CSV',
        id: 'export-to-csv',
      },
    ];
  }

  return (
    <div className="mb-4 flex flex-col gap-4">
      <div className="flex justify-between">
        <GlobalSearchBox searchInput={globalSearchValue} onSubmit={onGlobalSearchChange} />
        <div className="flex gap-4">
          {dateFilterOptions && (
            <Select<SelectOption<string>>
              defaultValue={defaultDateFilterOption}
              options={dateFilterOptions}
              onChange={onDateFilterChange}
              size="xs"
              className="min-w-[152px]"
            />
          )}
          {!isEmpty(_popoverActionMenuItems) && (
            <PopoverMenu
              variant="outline"
              trigger={
                <Button size={UISizes.sm} classNames={{ content: 'gap-2' }}>
                  Actions <DownIcon size={8} />
                </Button>
              }
              className="dark:bg-default-900 not-prose border-default-100 rounded-lg border bg-white"
              classNames={{ box: 'border-default-100' }}
              onSelect={onPopoverActionMenuSelect}
              closeOnSelect
              items={_popoverActionMenuItems}
              renderItem={item => {
                if (item?.id === 'export-to-csv') {
                  return (
                    <CSVDownloadLink data={exportData}>
                      <div className="text-default-900 dark:text-default-200 px-4 py-2">Export to CSV</div>
                    </CSVDownloadLink>
                  );
                } else {
                  return <MenuItem item={item} className="border-default-100 border border-b" />;
                }
              }}
            />
          )}
        </div>
      </div>
      <Divider className="my-0" />
      {statusFilters && (
        <div className="flex gap-4">
          <span>Show: </span>
          <RadioGroupList
            radioItems={statusFilters}
            selectedValue={selectedStatusValue}
            onChange={onStatusFilterChange}
          />
        </div>
      )}
    </div>
  );
};
